import { ReactNode, useCallback, useMemo } from 'react'

import staticStore from '@aytm/app/insights/dashboard/stores/static.store'

import { isBenchmark as isExternalDataCheck } from '@aytm/app/insights/dashboard/helpers/checks'
import { EditorType, ItemType } from '@aytm/app/insights/dashboard/models/types.ClientDTO'

interface GetCaptionMapperPropsType {
  key: string | number
}
interface GetEditorMapperPropsType {
  key: string | number
  itemIndex: number
  item: ItemType | null
  uid: string | null
  isBenchmark: boolean
  isEditing: boolean
  isExtraColumn: boolean
}
interface GetCellMapperPropsType {
  key: string | number
  itemIndex: number
  item: ItemType | null
  uid: string | null
  isBenchmark: boolean
  isEditing: boolean
  isExtraColumn: boolean
}

export type GetCaptionMapperType = { (props: GetCaptionMapperPropsType): ReactNode }
export type GetEditorMapperType = { (props: GetEditorMapperPropsType): ReactNode }
export type GetCellMapperType = { (props: GetCellMapperPropsType): ReactNode }

export interface UseRowPropsType {
  items: Array<ItemType>
  editor?: EditorType
}

export const useRow = ({ items: propsItems, editor: propsEditor }: UseRowPropsType) => {
  const { isSharedView } = staticStore

  const extraColumns = useMemo(() => (isSharedView ? 0 : propsEditor ? 1 : 0), [isSharedView, propsEditor])

  /**
   * Produces table row array by calling getCaption, getEditor and getCell methods
   * whenever it is needed for properer cell component rendering depending on 'editor'
   * state and current editing item index
   *
   * getCaption - Returns first column cell component.
   * getEditor - Returns cell editor for data column.
   * getCell - Returns data column cell component.
   * returns a row: an array containing cell components.
   */
  const getRow = useCallback(
    (getCaption: GetCaptionMapperType, getEditor: GetEditorMapperType, getCell: GetCellMapperType): ReactNode[] => {
      const editor = isSharedView ? null : propsEditor || null
      const items = propsItems || []
      const columnCount = (items.length || 0) + 1 + (editor ? 1 : 0)
      const editColumnIndex = editor && editor.itemIndex >= 0 ? editor.itemIndex + 1 : -1
      return Array.from({ length: columnCount }, (_v, index) => {
        const itemIndex = index - 1
        const item = items[itemIndex] || null
        const uid = item?.uid || null
        const isExtraColumn = editor && columnCount - 1 === index
        const isBenchmark = isExternalDataCheck({ uid }) || isExtraColumn
        const isEditing = ['edit', 'change'].includes(editor?.type)
        const key = `${0 === index ? 'first' : isExtraColumn ? 'extra' : uid}`

        if (0 === index) {
          // Render first caption column
          return getCaption({ key })
        } else if (isEditing && editColumnIndex === index) {
          // Render in place editor. By default the last column is always editing.
          // If getEditor returns null then render as common cell
          return (
            getEditor({ key, itemIndex, item, uid, isBenchmark, isEditing, isExtraColumn }) ||
            getCell({ key, itemIndex, item, uid, isBenchmark, isEditing, isExtraColumn })
          )
        } else {
          // Render cell subject/values data
          return getCell({ key, itemIndex, item, uid, isBenchmark, isEditing, isExtraColumn })
        }
      })
    },
    [isSharedView, propsEditor, propsItems]
  )

  return { getRow, extraColumns }
}
